import { useState, useRef,useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQuery } from 'react-query'
import { organizationsAPI } from '../../services/organizationsAPI'
import { productsAPI } from '../../services/productsAPI'
import api, { plansAPI } from '../../services/api'
import Card, { CardHeader, CardTitle, CardContent } from '../UI/Card'
import Button from '../UI/Button'
import Input from '../UI/Input'
import LoadingSpinner from '../UI/LoadingSpinner'
import ContactSalesModal from '../Modals/ContactSalesModal'
import { X, Check, ArrowRight, ArrowLeft, Building2, Package, Users, CheckCircle, Shield, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import PhoneFields from '../UI/PhoneFields'
import { combinePhone } from '../../utils/phone'

const getProductPriceLabel = (product, plans = []) => {
  if (product?.base_price != null && product.base_price !== '') {
    return `$${Number(product.base_price).toFixed(2)}`
  }

  const monthlyPrices = (plans || [])
    .map((plan) => Number(plan.monthly_price))
    .filter((price) => !Number.isNaN(price) && price > 0)

  if (monthlyPrices.length > 0) {
    return `From $${Math.min(...monthlyPrices).toFixed(2)}/mo`
  }

  const yearlyPrices = (plans || [])
    .map((plan) => Number(plan.yearly_price))
    .filter((price) => !Number.isNaN(price) && price > 0)

  if (yearlyPrices.length > 0) {
    return `From $${Math.min(...yearlyPrices).toFixed(2)}/yr`
  }

  if (plans?.length > 0) return `${plans.length} plan${plans.length === 1 ? '' : 's'}`
  return null
}

const getProductPricingModelLabel = (product, plans = []) => {
  if (product?.pricing_model === 'per_user') return 'Per User'
  if (product?.pricing_model === 'fixed') return 'Fixed Price'
  if (plans?.length > 0) return `${plans.length} plan${plans.length === 1 ? '' : 's'}`
  return 'Plans'
}

// Product Card Component to show roles
const ProductCard = ({ product, isSelected, roles, plans = [], onSelect }) => {
  const priceLabel = getProductPriceLabel(product, plans)
  return (
    <Card 
      className={`cursor-pointer transition-all ${
        isSelected
          ? 'ring-2 ring-blue-500 bg-blue-50'
          : 'hover:shadow-md'
      }`}
      onClick={onSelect}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-secondary-900">{product.name}</h4>
          {isSelected && (
            <CheckCircle className="h-5 w-5 text-blue-500" />
          )}
        </div>
        <p className="text-sm text-secondary-600 mb-3">{product.description}</p>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-secondary-900">
            {getProductPricingModelLabel(product, plans)}
          </span>
          <span className="text-sm text-secondary-600">
            {priceLabel || '—'}
          </span>
        </div>
        {/* Show roles for this product */}
        {roles.length > 0 && (
          <div className="mt-3 pt-3 border-t border-secondary-200">
            <div className="flex items-center mb-2">
              <Shield className="h-3 w-3 text-secondary-500 mr-1" />
              <span className="text-xs font-medium text-secondary-700">
                Available Roles ({roles.length})
              </span>
            </div>
            <div className="flex flex-wrap gap-1">
              {roles.slice(0, 3).map((role) => (
                <span 
                  key={role.id} 
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-700"
                >
                  {role.name}
                </span>
              ))}
              {roles.length > 3 && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-500">
                  +{roles.length - 3} more
                </span>
              )}
            </div>
          </div>
        )}
        {roles.length === 0 && isSelected && (
          <div className="mt-3 pt-3 border-t border-secondary-200">
            <span className="text-xs text-secondary-500">No roles available</span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const steps = [
  { id: 1, name: 'Organization Setup', icon: Building2, description: 'Basic organization information' },
  { id: 2, name: 'Product Selection', icon: Package, description: 'Choose products and roles' },
  { id: 3, name: 'User Invitations', icon: Users, description: 'Invite team members (admin auto-invited)' }
]

export default function CreateOrganizationWizard({ isOpen, onClose, onSuccess }) {
  const [currentStep, setCurrentStep] = useState(1)
  const [organizationData, setOrganizationData] = useState({})
  const [selectedProducts, setSelectedProducts] = useState([])
  const [productRoles, setProductRoles] = useState({})
  const [selectedProductRoles, setSelectedProductRoles] = useState({}) // Store selected role per product for organization email
  const [productPlans, setProductPlans] = useState({}) // Store plans for each product
  const [selectedProductPlans, setSelectedProductPlans] = useState({}) // Store selected plan per product
  const [enterpriseCustomPrices, setEnterpriseCustomPrices] = useState({}) // Store custom prices for Enterprise plans: key = `${productId}-${planId}`
  const [showContactSalesModal, setShowContactSalesModal] = useState(false)
  const [contactSalesPlan, setContactSalesPlan] = useState(null)
  const [invitations, setInvitations] = useState([{ 
    email: '', 
    role: '', 
    product_ids: [], 
    product_roles: {}
  }])
  const [invitationRoles, setInvitationRoles] = useState({})
  const [refreshingRoles, setRefreshingRoles] = useState({})
  const [isRefreshingAllRoles, setIsRefreshingAllRoles] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [basicAuthEnabled, setBasicAuthEnabled] = useState(false)
  const [ssoEnabled, setSsoEnabled] = useState(false)
  const [authSelectionError, setAuthSelectionError] = useState('')
  const [isValidatingSso, setIsValidatingSso] = useState(false)
  const fetchedProductMetaRef = useRef(new Set())

  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm({
    defaultValues: { phone_country: '+91' }
  })

  // Fetch only active products for step 2 (organization creation should not show inactive products)
  const { data: productsData, isLoading: productsLoading } = useQuery(
    ['products', 'organization-wizard', 'active'],
    () => productsAPI.getAll({ status: 'active', limit: 1000 }),
    { enabled: currentStep === 2 }
  )
  const products = productsData?.data?.data?.products || []

  // Fetch roles for a specific product (supports external API)
  const fetchProductRoles = async (productId) => {
    try {
      const response = await productsAPI.getRoles(productId)
      return response.data?.data?.roles || response.data?.roles || []
    } catch (error) {
      console.error('Error fetching product roles:', error)
      return []
    }
  }

  const applyRefreshedRoles = (productId, roles) => {
    setProductRoles((prev) => ({ ...prev, [productId]: roles }))

    setSelectedProductRoles((prev) => {
      const selectedId = prev[productId]
      if (!selectedId || roles.some((role) => String(role.id) === String(selectedId))) {
        return prev
      }
      const next = { ...prev }
      delete next[productId]
      return next
    })

    setInvitationRoles((prev) => {
      const next = { ...prev }
      Object.keys(next).forEach((key) => {
        if (key.endsWith(`-${productId}`)) {
          next[key] = roles
        }
      })
      return next
    })

    setInvitations((prev) =>
      prev.map((invitation) => {
        const selectedId = invitation.product_roles?.[productId]
        if (!selectedId || roles.some((role) => String(role.id) === String(selectedId))) {
          return invitation
        }
        return {
          ...invitation,
          product_roles: {
            ...invitation.product_roles,
            [productId]: null
          }
        }
      })
    )
  }

  const refreshProductRoles = async (productId, { quiet } = {}) => {
    if (!productId) return []
    setRefreshingRoles((prev) => ({ ...prev, [productId]: true }))
    try {
      const roles = await fetchProductRoles(productId)
      applyRefreshedRoles(productId, roles)
      if (!quiet) {
        toast.success(roles.length ? 'Roles refreshed' : 'No roles found for this product')
      }
      return roles
    } catch (error) {
      console.error('Error refreshing product roles:', error)
      if (!quiet) toast.error('Failed to refresh roles')
      return []
    } finally {
      setRefreshingRoles((prev) => ({ ...prev, [productId]: false }))
    }
  }

  const refreshAllProductRoles = async (productIds) => {
    const ids = [...new Set((productIds || []).filter(Boolean))]
    if (ids.length === 0) return
    setIsRefreshingAllRoles(true)
    try {
      await Promise.all(ids.map((id) => refreshProductRoles(id, { quiet: true })))
      toast.success('Roles refreshed')
    } finally {
      setIsRefreshingAllRoles(false)
    }
  }

  // Fetch plans for a specific product
  const fetchProductPlans = async (productId) => {
    try {
      const response = await plansAPI.getByProduct(productId)
      return response.data?.data?.plans || response.data?.plans || []
    } catch (error) {
      console.error('Error fetching product plans:', error)
      return []
    }
  }

  // Calculate savings percentage for yearly pricing
  const calculateSavings = (monthlyPrice, yearlyPrice) => {
    if (!monthlyPrice || !yearlyPrice || monthlyPrice === 0) return null
    const monthlyYearlyTotal = monthlyPrice * 12
    if (yearlyPrice >= monthlyYearlyTotal) return null
    const savings = monthlyYearlyTotal - yearlyPrice
    const savingsPercent = Math.round((savings / monthlyYearlyTotal) * 100)
    return savingsPercent > 0 ? savingsPercent : null
  }

  // Pre-fetch roles and plans for all products when products are loaded
  useEffect(() => {
    if (products.length === 0 || currentStep !== 2) return

    products.forEach((product) => {
      if (fetchedProductMetaRef.current.has(product.id)) return
      fetchedProductMetaRef.current.add(product.id)

      Promise.all([
        fetchProductRoles(product.id),
        fetchProductPlans(product.id)
      ]).then(([roles, plans]) => {
        setProductRoles((prev) => ({ ...prev, [product.id]: roles }))
        setProductPlans((prev) => ({ ...prev, [product.id]: plans }))
      })
    })
  }, [products, currentStep])

  // Fetch roles for an invitation when a product is chosen
  const fetchRolesForInvitation = async (index, productId) => {
    if (!productId) {
      setInvitationRoles(prev => ({ ...prev, [`${index}-${productId}`]: [] }))
      return
    }
    const roles = await fetchProductRoles(productId)
    setInvitationRoles(prev => ({ ...prev, [`${index}-${productId}`]: roles }))
  }

  // Create organization mutation
  const createOrganizationMutation = useMutation(organizationsAPI.create, {
    onSuccess: (data) => {
      toast.success('Organization created successfully!')
      onSuccess && onSuccess(data)
      handleClose()
    },
    onError: (error) => {
      console.error('Error creating organization:', error)
      const errorData = error.response?.data
      const errorMessage = errorData?.message || error.message || 'Failed to create organization'
      toast.error(errorMessage)
      if (errorData?.error === 'SSO_VALIDATION_FAILED') {
        setAuthSelectionError(errorMessage)
        setCurrentStep(1)
      }
    }
  })

  const handleStep1Submit = async (data) => {
    if (!basicAuthEnabled && !ssoEnabled) {
      setAuthSelectionError('Basic Authentication or Enable SSO is required')
      toast.error('Select Basic Authentication or Enable SSO to create the organization in GCP')
      return
    }
    setAuthSelectionError('')

    if (ssoEnabled) {
      setIsValidatingSso(true)
      try {
        await api.post('/sso-configurations/validate', {
          provider: data.sso_provider,
          client_id: data.sso_client_id,
          client_secret: data.sso_client_secret
        })
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'Invalid SSO credentials. Organization was not created.'
        setAuthSelectionError(errorMessage)
        toast.error(errorMessage)
        return
      } finally {
        setIsValidatingSso(false)
      }
    }

    // Map checkbox states to form data
    // SSO always includes basic auth so the org/users can be created in GCP for product-app login
    if (ssoEnabled) {
      data.sso_email_domain = (data.sso_email_domain || '').trim().toLowerCase()
      data.basic_auth_enabled = true
    } else if (basicAuthEnabled) {
      data.sso_provider = 'email_password'
      data.sso_client_id = ''
      data.sso_client_secret = ''
      data.sso_email_domain = ''
      data.basic_auth_enabled = true
    }
    
    data.phone = combinePhone(data.phone_country, data.phone_number)
    delete data.phone_country
    delete data.phone_number
    setOrganizationData(data)
    setCurrentStep(2)
  }

  const handleStep2Submit = (data) => {
    setSelectedProducts(data.selectedProducts || [])
    setCurrentStep(3)
  }

  const handleStep3Submit = () => {
    handleFinalSubmit()
  }

  const handleFinalSubmit = async () => {
    setIsSubmitting(true)
    try {
      // Automatically add the organization email as an admin invitation
      // Include all selected products for the admin with selected roles from Step 2
      const adminProductIds = selectedProducts.map(p => p.id || p)
      const adminProductRoles = {}
      
      // Use selected roles from Step 2 for admin invitation
      selectedProducts.forEach(product => {
        const productId = product.id || product
        // Get the selected role ID from Step 2, or null if no role selected
        adminProductRoles[productId] = selectedProductRoles[productId] || null
      })
      
      const adminInvitation = {
        email: organizationData.email,
        role: 'admin',
        product_ids: adminProductIds,
        product_roles: adminProductRoles,
        message: 'You have been invited as the organization administrator.'
      }
      
      // Transform invitations to include products_data structure
      const transformedInvitations = invitations.map(inv => {
        const productsData = (inv.product_ids || []).map(productId => {
          // Get the selected role ID (single role per product)
          const selectedRoleId = inv.product_roles?.[productId]
          // Get the selected plan ID for this product (from step 2)
          const selectedPlanId = selectedProductPlans[productId] || null
          // Convert to array format for backend (empty array if no role selected)
          const selectedRoles = selectedRoleId ? [selectedRoleId] : []
          
          return {
            product_id: productId,
            selected_roles: selectedRoles,
            plan_id: selectedPlanId
          }
        })
        
        return {
          email: inv.email,
          role: inv.role,
          product_ids: inv.product_ids || [],
          products_data: productsData,
          message: inv.message || ''
        }
      })
      
      // Add admin invitation to the beginning
      const adminProductsData = adminProductIds.map(productId => {
        const selectedRoleId = adminProductRoles[productId]
        const selectedPlanId = selectedProductPlans[productId] || null
        // Find the role name from productRoles state if available
        const roles = productRoles[productId] || []
        const selectedRole = roles.find(r => r.id === selectedRoleId)
        const roleName = selectedRole ? selectedRole.name : null
        
        return {
          product_id: productId,
          selected_roles: selectedRoleId ? [selectedRoleId] : [],
          plan_id: selectedPlanId,
          _role_id: selectedRoleId || null,
          _role_name: roleName || null
        }
      })
      
      const finalAdminInvitation = {
        ...adminInvitation,
        products_data: adminProductsData
      }
      
      const finalInvitations = [finalAdminInvitation, ...transformedInvitations]
      
      const finalData = {
        ...organizationData,
        selectedProducts,
        selectedProductPlans, // Include selected plans
        invitations: finalInvitations
      }
      
      await createOrganizationMutation.mutateAsync(finalData)
    } catch (error) {
      // Error handled in mutation
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      setCurrentStep(1)
      setOrganizationData({})
      setSelectedProducts([])
      setProductRoles({})
      setSelectedProductRoles({})
      setProductPlans({})
      setSelectedProductPlans({})
      setEnterpriseCustomPrices({})
      fetchedProductMetaRef.current = new Set()
      setInvitations([])
      setInvitationRoles({})
      setRefreshingRoles({})
      setIsRefreshingAllRoles(false)
      setBasicAuthEnabled(false)
      setSsoEnabled(false)
      setAuthSelectionError('')
      setIsValidatingSso(false)
      reset()
      onClose()
    }
  }

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const addInvitation = () => {
    setInvitations([...invitations, { 
      email: '', 
      role: '', 
      product_ids: [], 
      product_roles: {}
    }])
  }

  const removeInvitation = (index) => {
    setInvitations(invitations.filter((_, i) => i !== index))
  }

  const updateInvitation = (index, field, value) => {
    const updated = [...invitations]
    updated[index] = { ...updated[index], [field]: value }
    setInvitations(updated)
  }

  // Handle multiple product selection
  const handleProductSelection = (index, productId, isSelected) => {
    const updated = [...invitations]
    const selectedProductIds = [...(updated[index].product_ids || [])]
    
    if (isSelected) {
      if (!selectedProductIds.includes(productId)) {
        selectedProductIds.push(productId)
        // Fetch roles for the newly selected product
        fetchRolesForInvitation(index, productId)
        // Initialize product_roles for this product (null = no role selected)
        updated[index].product_roles = {
          ...updated[index].product_roles,
          [productId]: null
        }
      }
    } else {
      const productIndex = selectedProductIds.indexOf(productId)
      if (productIndex > -1) {
        selectedProductIds.splice(productIndex, 1)
        // Remove roles for this product
        const newProductRoles = { ...updated[index].product_roles }
        delete newProductRoles[productId]
        updated[index].product_roles = newProductRoles
      }
    }
    
    updated[index].product_ids = selectedProductIds
    setInvitations(updated)
  }

  // Handle role selection (radio button) - only one role per product
  const handleRoleSelection = (index, productId, roleId) => {
    const updated = [...invitations]
    const productRoles = { ...updated[index].product_roles }
    
    // Store only the selected role ID (single selection)
    productRoles[productId] = roleId
    
    updated[index].product_roles = productRoles
    setInvitations(updated)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <div className="flex items-center space-x-2">
            <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <Building2 className="h-4 w-4 text-blue-600" />
            </div>
            <CardTitle>Create New Organization</CardTitle>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>

        <CardContent>
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => {
                const Icon = step.icon
                const isActive = currentStep === step.id
                const isCompleted = currentStep > step.id
                
                return (
                  <div key={step.id} className="flex items-center">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                      isCompleted 
                        ? 'bg-green-500 border-green-500 text-white' 
                        : isActive 
                          ? 'bg-blue-500 border-blue-500 text-white'
                          : 'bg-gray-100 border-gray-300 text-gray-500'
                    }`}>
                      {isCompleted ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                    </div>
                    <div className="ml-3">
                      <p className={`text-sm font-medium ${
                        isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {step.name}
                      </p>
                      <p className="text-xs text-gray-500">{step.description}</p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className={`flex-1 h-0.5 mx-4 ${
                        isCompleted ? 'bg-green-500' : 'bg-gray-200'
                      }`} />
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Step Content */}
          {currentStep === 1 && (
            <form onSubmit={handleSubmit(handleStep1Submit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Organization Name *
                  </label>
                  <input
                    {...register('name', { required: 'Organization name is required' })}
                    type="text"
                    className="input w-full"
                    placeholder="Enter organization name"
                    disabled={isSubmitting}
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Email *
                  </label>
                  <input
                    {...register('email', { 
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email address'
                      }
                    })}
                    type="email"
                    className="input w-full"
                    placeholder="Enter organization email"
                    disabled={isSubmitting}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <PhoneFields register={register} errors={errors} disabled={isSubmitting} required />

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Website
                  </label>
                  <input
                    {...register('website')}
                    type="url"
                    className="input w-full"
                    placeholder="https://example.com"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Industry
                  </label>
                  <select
                    {...register('industry')}
                    className="input w-full"
                    disabled={isSubmitting}
                  >
                    <option value="">Select industry</option>
                    <option value="technology">Technology</option>
                    <option value="healthcare">Healthcare</option>
                    <option value="finance">Finance</option>
                    <option value="education">Education</option>
                    <option value="retail">Retail</option>
                    <option value="manufacturing">Manufacturing</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Employee Count
                  </label>
                  <input
                    {...register('employee_count', { 
                      valueAsNumber: true,
                      min: { value: 1, message: 'Must be at least 1' }
                    })}
                    type="number"
                    min="1"
                    className="input w-full"
                    placeholder="Number of employees"
                    disabled={isSubmitting}
                  />
                  {errors.employee_count && (
                    <p className="mt-1 text-sm text-red-600">{errors.employee_count.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Location
                  </label>
                  <input
                    {...register('location')}
                    type="text"
                    className="input w-full"
                    placeholder="City, Country"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Authentication Configuration Section */}
              <div className="mt-8 pt-6 border-t border-secondary-200">
                <h3 className="text-lg font-semibold text-secondary-900 mb-4">SSO Configuration *</h3>
                <div className="flex items-center space-x-6 mb-6">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={basicAuthEnabled}
                      onChange={(e) => {
                        const checked = e.target.checked
                        if (!checked && ssoEnabled) {
                          return
                        }
                        setBasicAuthEnabled(checked)
                        if (checked) {
                          setAuthSelectionError('')
                        }
                      }}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      disabled={isSubmitting || ssoEnabled}
                    />
                    <span className="ml-2 text-sm font-medium text-secondary-700">
                      Basic Authentication
                    </span>
                  </label>

                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={ssoEnabled}
                      onChange={(e) => {
                        const checked = e.target.checked
                        setSsoEnabled(checked)
                        if (checked) {
                          setAuthSelectionError('')
                          setBasicAuthEnabled(true)
                        } else {
                          setValue('sso_provider', '')
                          setValue('sso_client_id', '')
                          setValue('sso_client_secret', '')
                          setValue('sso_email_domain', '')
                        }
                      }}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                      disabled={isSubmitting}
                    />
                    <span className="ml-2 text-sm font-medium text-secondary-700">
                      Enable SSO
                    </span>
                  </label>
                </div>
                {authSelectionError && (
                  <p className="mb-4 text-sm text-red-600">
                    {authSelectionError}
                  </p>
                )}
                {ssoEnabled && (
                  <div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Provider
                        </label>
                        <select
                          {...register('sso_provider', {
                            required: ssoEnabled ? 'SSO Provider is required' : false
                          })}
                          className="input w-full"
                          disabled={isSubmitting}
                        >
                          <option value="">Select SSO Provider</option>
                          <option value="google">Google</option>
                          <option value="microsoft">Microsoft</option>
                          <option value="azure">Azure AD</option>
                        </select>
                        {errors.sso_provider && (
                          <p className="mt-1 text-sm text-red-600">{errors.sso_provider.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Client ID
                        </label>
                        <input
                          {...register('sso_client_id', {
                            required: ssoEnabled ? 'Client ID is required' : false
                          })}
                          type="text"
                          className="input w-full"
                          placeholder="Type here"
                          disabled={isSubmitting}
                        />
                        {errors.sso_client_id && (
                          <p className="mt-1 text-sm text-red-600">{errors.sso_client_id.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Secret ID
                        </label>
                        <input
                          {...register('sso_client_secret', {
                            required: ssoEnabled ? 'Secret ID is required' : false
                          })}
                          type="password"
                          className="input w-full"
                          placeholder="Type here"
                          disabled={isSubmitting}
                        />
                        {errors.sso_client_secret && (
                          <p className="mt-1 text-sm text-red-600">{errors.sso_client_secret.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-secondary-700 mb-2">
                          Email Domain *
                        </label>
                        <input
                          {...register('sso_email_domain', {
                            required: ssoEnabled ? 'Email domain is required' : false,
                            validate: (value) => {
                              if (!ssoEnabled) return true
                              const domain = (value || '').trim().toLowerCase()
                              const domainPattern = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*\.[a-z]{2,}$/
                              return domainPattern.test(domain) || 'Please enter a valid email domain (e.g., comptivia.com)'
                            }
                          })}
                          type="text"
                          className="input w-full"
                          placeholder="comptivia.com"
                          disabled={isSubmitting}
                        />
                        {errors.sso_email_domain && (
                          <p className="mt-1 text-sm text-red-600">{errors.sso_email_domain.message}</p>
                        )}
                        <p className="mt-1 text-xs text-secondary-500">
                          Domain used for SSO (e.g., comptivia.com)
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4 border-t border-secondary-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting || isValidatingSso}
                  className="min-w-[100px]"
                >
                  {isValidatingSso ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      Next
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-secondary-900 mb-2">Select Products</h3>
                  <p className="text-sm text-secondary-600">
                    Choose the products and roles you want to include in this organization.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => refreshAllProductRoles(products.map((product) => product.id))}
                  disabled={isRefreshingAllRoles || productsLoading || products.length === 0}
                  title="Refresh product roles"
                  className="whitespace-nowrap"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshingAllRoles ? 'animate-spin' : ''}`} />
                  Refresh roles
                </Button>
              </div>

              {productsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <Card 
                      key={product.id} 
                      className={`cursor-pointer transition-all ${
                        selectedProducts.some(p => p.id === product.id)
                          ? 'ring-2 ring-blue-500 bg-blue-50'
                          : 'hover:shadow-md'
                      }`}
                      onClick={async () => {
                        const isSelected = selectedProducts.some(p => p.id === product.id)
                        if (isSelected) {
                          setSelectedProducts(selectedProducts.filter(p => p.id !== product.id))
                          const newSelectedProductRoles = { ...selectedProductRoles }
                          delete newSelectedProductRoles[product.id]
                          setSelectedProductRoles(newSelectedProductRoles)
                          const newSelectedProductPlans = { ...selectedProductPlans }
                          delete newSelectedProductPlans[product.id]
                          setSelectedProductPlans(newSelectedProductPlans)
                          const newEnterpriseCustomPrices = { ...enterpriseCustomPrices }
                          Object.keys(newEnterpriseCustomPrices).forEach(key => {
                            if (key.startsWith(`${product.id}-`)) {
                              delete newEnterpriseCustomPrices[key]
                            }
                          })
                          setEnterpriseCustomPrices(newEnterpriseCustomPrices)
                        } else {
                          setSelectedProducts(prev => {
                            if (prev.some(p => p.id === product.id)) return prev
                            return [...prev, { id: product.id, name: product.name }]
                          })
                          if (productRoles[product.id] === undefined || productPlans[product.id] === undefined) {
                            const [roles, plans] = await Promise.all([
                              fetchProductRoles(product.id),
                              fetchProductPlans(product.id)
                            ])
                            setProductRoles(prev => ({ ...prev, [product.id]: roles }))
                            setProductPlans(prev => ({ ...prev, [product.id]: plans }))
                          }
                        }
                      }}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-secondary-900">{product.name}</h4>
                          {selectedProducts.some(p => p.id === product.id) && (
                            <CheckCircle className="h-5 w-5 text-blue-500" />
                          )}
                        </div>
                        <p className="text-sm text-secondary-600 mb-3">{product.description}</p>
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-sm font-medium text-secondary-900">
                            {getProductPricingModelLabel(product, productPlans[product.id] || [])}
                          </span>
                          <span className="text-sm text-secondary-600">
                            {getProductPriceLabel(product, productPlans[product.id] || []) || '—'}
                          </span>
                        </div>
                        {/* Show roles for this product */}
                        <div
                          className="mt-3 pt-3 border-t border-secondary-200"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              <Shield className="h-3 w-3 text-secondary-500 mr-1" />
                              <span className="text-xs font-medium text-secondary-700">
                                Available Roles ({productRoles[product.id]?.length || 0})
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={() => refreshProductRoles(product.id)}
                              disabled={refreshingRoles[product.id]}
                              title="Refresh roles"
                              className="inline-flex items-center text-secondary-500 hover:text-blue-600 disabled:opacity-50"
                            >
                              <RefreshCw className={`h-3.5 w-3.5 ${refreshingRoles[product.id] ? 'animate-spin' : ''}`} />
                            </button>
                          </div>
                          {productRoles[product.id] === undefined || refreshingRoles[product.id] ? (
                            <span className="text-xs text-secondary-500">
                              {refreshingRoles[product.id] ? 'Refreshing roles...' : 'Loading roles...'}
                            </span>
                          ) : productRoles[product.id].length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {productRoles[product.id].slice(0, 3).map((role) => (
                                <span 
                                  key={role.id} 
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-700"
                                >
                                  {role.name}
                                </span>
                              ))}
                              {productRoles[product.id].length > 3 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary-100 text-secondary-500">
                                  +{productRoles[product.id].length - 3} more
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-secondary-500">No roles available</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Role Selection for Selected Products */}
              {selectedProducts.length > 0 && (
                <div className="mt-8">
                  <div className="mb-6 flex items-start justify-between gap-4">
                    <div>
                      <h4 className="text-lg font-semibold text-secondary-900 mb-2">Select Roles</h4>
                      <p className="text-sm text-secondary-600">
                        Select a role for each product. This role will be assigned to the organization administrator ({organizationData.email}).
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => refreshAllProductRoles(selectedProducts.map((product) => product.id))}
                      disabled={isRefreshingAllRoles || selectedProducts.length === 0}
                      title="Refresh product roles"
                      className="whitespace-nowrap"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshingAllRoles ? 'animate-spin' : ''}`} />
                      Refresh roles
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {selectedProducts.map((selectedProduct, index) => {
                      const roles = productRoles[selectedProduct.id]
                      const rolesLoading = roles === undefined
                      const rolesList = roles || []
                      return (
                        <Card key={selectedProduct.id} className="overflow-hidden">
                          <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-secondary-200">
                            <CardTitle className="text-base flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <span className="inline-flex items-center justify-center w-8 h-8 bg-blue-500 text-white text-sm font-semibold rounded-full">
                                  {index + 1}
                                </span>
                                <div>
                                  <div className="font-semibold text-secondary-900">{selectedProduct.name} Roles</div>
                                  <div className="text-xs text-secondary-500 mt-0.5">
                                    {rolesLoading || refreshingRoles[selectedProduct.id]
                                      ? 'Loading roles...'
                                      : `${rolesList.length} ${rolesList.length === 1 ? 'role' : 'roles'} available`}
                                  </div>
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => refreshProductRoles(selectedProduct.id)}
                                disabled={refreshingRoles[selectedProduct.id] || isSubmitting}
                                title="Refresh roles"
                              >
                                <RefreshCw className={`h-4 w-4 ${refreshingRoles[selectedProduct.id] ? 'animate-spin' : ''}`} />
                              </Button>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6">
                            {rolesLoading || refreshingRoles[selectedProduct.id] ? (
                              <div className="flex items-center justify-center py-8">
                                <LoadingSpinner size="md" />
                              </div>
                            ) : rolesList.length > 0 ? (
                              <div className="space-y-2">
                                {rolesList.map((role) => {
                                  const isRoleSelected = selectedProductRoles[selectedProduct.id] === role.id
                                  return (
                                    <label
                                      key={role.id}
                                      className={`flex items-center justify-between text-sm p-3 rounded-lg border cursor-pointer transition-colors ${
                                        isRoleSelected
                                          ? 'border-blue-500 bg-blue-50 shadow-sm'
                                          : 'border-secondary-200 bg-white hover:border-blue-300'
                                      }`}
                                    >
                                      <div className="flex items-center space-x-3 flex-1">
                                        <div className="flex-shrink-0">
                                          <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                            isRoleSelected ? 'bg-blue-500' : 'bg-blue-100 group-hover:bg-blue-200'
                                          }`}>
                                            <Shield className={`h-4 w-4 ${isRoleSelected ? 'text-white' : 'text-blue-600'}`} />
                                          </div>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center justify-between mb-1">
                                            <span className="text-sm font-semibold text-secondary-900">
                                              {role.name}
                                            </span>
                                          </div>
                                          {role.description && (
                                            <p className="text-xs text-secondary-500 line-clamp-1 leading-relaxed">
                                              {role.description}
                                            </p>
                                          )}
                                          {!role.description && (
                                            <p className="text-xs text-secondary-400 italic">
                                              No description
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                      <input
                                        type="radio"
                                        name={`product-role-${selectedProduct.id}`}
                                        value={role.id}
                                        checked={isRoleSelected}
                                        onChange={() => {
                                          setSelectedProductRoles({
                                            ...selectedProductRoles,
                                            [selectedProduct.id]: role.id
                                          })
                                        }}
                                        className="text-blue-600 focus:ring-blue-500 ml-3"
                                        disabled={isSubmitting}
                                      />
                                    </label>
                                  )
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-8">
                                <Shield className="h-12 w-12 text-secondary-300 mx-auto mb-3" />
                                <p className="text-sm font-medium text-secondary-600">No roles available</p>
                                <p className="text-xs text-secondary-500 mt-1">This product doesn't have any roles configured yet.</p>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="mt-4"
                                  onClick={() => refreshProductRoles(selectedProduct.id)}
                                  disabled={refreshingRoles[selectedProduct.id]}
                                >
                                  <RefreshCw className={`h-4 w-4 mr-2 ${refreshingRoles[selectedProduct.id] ? 'animate-spin' : ''}`} />
                                  Refresh roles
                                </Button>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Plan Selection for Selected Products */}
              {selectedProducts.length > 0 && (
                <div className="mt-8">
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-secondary-900 mb-2">Select Plans</h4>
                    <p className="text-sm text-secondary-600">
                      Select a plan for each product. This plan will be used when creating subscriptions for this organization.
                    </p>
                  </div>
                  <div className="space-y-4">
                    {selectedProducts.map((selectedProduct, index) => {
                      const plans = productPlans[selectedProduct.id]
                      const plansLoading = plans === undefined
                      const plansList = plans || []
                      return (
                        <Card key={`plan-${selectedProduct.id}`} className="overflow-hidden">
                          <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-secondary-200">
                            <CardTitle className="text-base flex items-center justify-between">
                              <div className="flex items-center space-x-3">
                                <span className="inline-flex items-center justify-center w-8 h-8 bg-green-500 text-white text-sm font-semibold rounded-full">
                                  {index + 1}
                                </span>
                                <div>
                                  <div className="font-semibold text-secondary-900">{selectedProduct.name} Plans</div>
                                  <div className="text-xs text-secondary-500 mt-0.5">
                                    {plansLoading
                                      ? 'Loading plans...'
                                      : `${plansList.length} ${plansList.length === 1 ? 'plan' : 'plans'} available`}
                                  </div>
                                </div>
                              </div>
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="p-6">
                            {plansLoading ? (
                              <div className="flex items-center justify-center py-8">
                                <LoadingSpinner size="md" />
                              </div>
                            ) : plansList.length > 0 ? (
                              <div className="space-y-2">
                                {plansList.map((plan) => {
                                  const isPlanSelected = selectedProductPlans[selectedProduct.id] === plan.id
                                  const customPriceKey = `${selectedProduct.id}-${plan.id}`
                                  const customPrice = enterpriseCustomPrices[customPriceKey]
                                  
                                  return (
                                    <div key={plan.id} className="space-y-2">
                                      <label
                                        className={`flex items-center justify-between text-sm p-3 rounded-lg border cursor-pointer transition-colors ${
                                          isPlanSelected
                                            ? 'border-green-500 bg-green-50 shadow-sm'
                                            : 'border-secondary-200 bg-white hover:border-green-300'
                                        }`}
                                      >
                                        <div className="flex items-center space-x-3 flex-1">
                                          <div className="flex-shrink-0">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                                              isPlanSelected ? 'bg-green-500' : 'bg-green-100 group-hover:bg-green-200'
                                            }`}>
                                              <Package className={`h-4 w-4 ${isPlanSelected ? 'text-white' : 'text-green-600'}`} />
                                            </div>
                                          </div>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                              <div className="flex items-center space-x-2">
                                                <span className="text-sm font-semibold text-secondary-900">
                                                  {plan.name}
                                                </span>
                                                {plan.is_enterprise && (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                                    Enterprise
                                                  </span>
                                                )}
                                              </div>
                                              {plan.monthly_price > 0 && !plan.is_enterprise ? (
                                                <div className="text-right">
                                                  <div className="text-sm font-medium text-secondary-700">
                                                    {new Intl.NumberFormat('en-US', {
                                                      style: 'currency',
                                                      currency: plan.currency || 'USD'
                                                    }).format(plan.monthly_price)}/month
                                                  </div>
                                                  {plan.yearly_price > 0 && (() => {
                                                    const savingsPercent = calculateSavings(plan.monthly_price, plan.yearly_price)
                                                    return (
                                                      <div className="text-xs text-secondary-600">
                                                        {new Intl.NumberFormat('en-US', {
                                                          style: 'currency',
                                                          currency: plan.currency || 'USD'
                                                        }).format(plan.yearly_price)}/year
                                                        {savingsPercent && (
                                                          <span className="text-green-600 ml-1">(save {savingsPercent}%)</span>
                                                        )}
                                                      </div>
                                                    )
                                                  })()}
                                                </div>
                                              ) : null}
                                            </div>
                                            {plan.description && (
                                              <p className="text-xs text-secondary-500 line-clamp-1 leading-relaxed">
                                                {plan.description}
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                        <input
                                          type="radio"
                                          name={`product-plan-${selectedProduct.id}`}
                                          value={plan.id}
                                          checked={isPlanSelected}
                                          onChange={() => {
                                            setSelectedProductPlans({
                                              ...selectedProductPlans,
                                              [selectedProduct.id]: plan.id
                                            })
                                          }}
                                          className="text-green-600 focus:ring-green-500 ml-3"
                                          disabled={isSubmitting}
                                        />
                                      </label>
                                      
                                    </div>
                                  )
                                })}
                              </div>
                            ) : (
                              <div className="text-center py-8">
                                <Package className="h-12 w-12 text-secondary-300 mx-auto mb-3" />
                                <p className="text-sm font-medium text-secondary-600">No plans available</p>
                                <p className="text-xs text-secondary-500 mt-1">This product doesn't have any plans configured yet.</p>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      )
                    })}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-secondary-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={isSubmitting}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
                <Button
                  onClick={nextStep}
                  disabled={isSubmitting || selectedProducts.length === 0}
                  className="min-w-[100px]"
                >
                  Next
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-secondary-900 mb-2">Invite Team Members</h3>
                <p className="text-sm text-secondary-600 mb-4">
                  Invite users to join your organization. You can add more users later.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-800">Organization Admin</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        <strong>{organizationData.email}</strong> will automatically be invited as the organization administrator.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {invitations.map((invitation, index) => (
                  <Card key={index}>
                    <CardContent className="p-4">
                      <div className="flex items-end gap-4">
                        <div className="flex-1">
                          <label className="block text-sm font-bold text-secondary-700 mb-2">
                            Email Address
                          </label>
                          <input
                            type="email"
                            value={invitation.email}
                            onChange={(e) => updateInvitation(index, 'email', e.target.value)}
                            className="input w-full"
                            placeholder="user@example.com"
                            disabled={isSubmitting}
                          />
                        </div>
                        {/* Role dropdown commented out */}
                        {/* <div>
                          <label className="block text-sm font-medium text-secondary-700 mb-2">
                            Role
                          </label>
                          <select
                            value={invitation.role}
                            onChange={(e) => updateInvitation(index, 'role', e.target.value)}
                            className="input w-full"
                            disabled={isSubmitting}
                          >
                            <option value="">Select role</option>
                            <option value="admin">Administrator</option>
                            <option value="manager">Manager</option>
                            <option value="user">User</option>
                          </select>
                        </div> */}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => removeInvitation(index)}
                          disabled={isSubmitting}
                          className="text-red-600 hover:text-red-700 whitespace-nowrap"
                        >
                          Remove
                        </Button>
                      </div>

                      {/* Product Selection - Multiple Products */}
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-bold text-secondary-700">
                            Products *
                          </label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => refreshAllProductRoles(invitation.product_ids?.length ? invitation.product_ids : selectedProducts.map((p) => p.id || p))}
                            disabled={isRefreshingAllRoles || isSubmitting || selectedProducts.length === 0}
                            title="Refresh product roles"
                          >
                            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isRefreshingAllRoles ? 'animate-spin' : ''}`} />
                            Refresh roles
                          </Button>
                        </div>
                        {selectedProducts.length > 0 ? (
                          <div className="space-y-3">
                            {selectedProducts.map((p) => {
                              const product = (products || []).find(pr => pr.id === (p.id || p))
                              const productId = p.id || p
                              const productName = product?.name || p.name || productId || 'Product'
                              const isSelected = invitation.product_ids?.includes(productId) || false
                              const roles = invitationRoles[`${index}-${productId}`] || []
                              const selectedRoleId = invitation.product_roles?.[productId] || null

                              return (
                                <div
                                  key={productId}
                                  className={`border rounded-lg transition-all ${
                                    isSelected ? 'border-blue-400 bg-blue-50 shadow-sm' : 'border-secondary-200 bg-white'
                                  }`}
                                >
                                  <div className="flex items-start space-x-3 p-3">
                                    <input
                                      type="checkbox"
                                      id={`product-${index}-${productId}`}
                                      checked={isSelected}
                                      onChange={(e) => handleProductSelection(index, productId, e.target.checked)}
                                      className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                      disabled={isSubmitting}
                                    />
                                    <label 
                                      htmlFor={`product-${index}-${productId}`}
                                      className="flex-1 text-sm text-secondary-800 cursor-pointer"
                                    >
                                      <div className="flex items-center justify-between">
                                        <span className="font-semibold">{productName}</span>
                                        {roles.length > 0 && (
                                          <span className="text-xs text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                                            {roles.length} roles
                                          </span>
                                        )}
                                      </div>
                                      {product?.description && (
                                        <span className="text-xs text-secondary-500 block mt-0.5">
                                          {product.description}
                                        </span>
                                      )}
                                    </label>
                                  </div>

                                  {isSelected && (
                                    <div className="px-4 pb-4">
                                      <div className="flex items-center justify-between text-xs font-semibold text-secondary-600 mb-2">
                                        <div className="flex items-center space-x-2">
                                          <Shield className="h-3 w-3 text-blue-500" />
                                          <span>Available Roles</span>
                                        </div>
                                        <button
                                          type="button"
                                          onClick={() => refreshProductRoles(productId)}
                                          disabled={refreshingRoles[productId] || isSubmitting}
                                          title="Refresh roles"
                                          className="inline-flex items-center text-secondary-500 hover:text-blue-600 disabled:opacity-50"
                                        >
                                          <RefreshCw className={`h-3.5 w-3.5 ${refreshingRoles[productId] ? 'animate-spin' : ''}`} />
                                        </button>
                                      </div>
                                      {refreshingRoles[productId] ? (
                                        <div className="flex items-center py-2 text-xs text-secondary-500">
                                          <LoadingSpinner size="sm" />
                                          <span className="ml-2">Refreshing roles...</span>
                                        </div>
                                      ) : roles.length > 0 ? (
                                        <div className="space-y-2">
                                          {roles.map((role) => {
                                            const isRoleSelected = selectedRoleId === role.id
                                            return (
                                              <label
                                                key={role.id}
                                                className={`flex items-center justify-between text-sm p-3 rounded-lg border cursor-pointer transition-colors ${
                                                  isRoleSelected
                                                    ? 'border-blue-500 bg-white shadow-sm'
                                                    : 'border-secondary-200 bg-white hover:border-blue-300'
                                                }`}
                                              >
                                                <div className="flex items-center space-x-2">
                                                  <span className={`w-2 h-2 rounded-full ${
                                                    isRoleSelected ? 'bg-blue-600' : 'bg-secondary-300'
                                                  }`} />
                                                  <span className="font-medium text-secondary-800">{role.name}</span>
                                                </div>
                                                <input
                                                  type="radio"
                                                  name={`inv-role-${index}-${productId}`}
                                                  value={role.id}
                                                  checked={isRoleSelected}
                                                  onChange={() => handleRoleSelection(index, productId, role.id)}
                                                  className="text-blue-600 focus:ring-blue-500"
                                                  disabled={isSubmitting}
                                                />
                                              </label>
                                            )
                                          })}
                                        </div>
                                      ) : (
                                        <p className="text-xs text-secondary-500 italic">No roles configured for this product.</p>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <div className="border border-secondary-300 rounded-md p-3 bg-secondary-50">
                            <p className="text-sm text-secondary-600">
                              No products selected. Please select products in step 2.
                            </p>
                          </div>
                        )}
                        {invitation.product_ids?.length > 0 && (
                          <p className="mt-2 text-sm text-secondary-600">
                            {invitation.product_ids.length} product{invitation.product_ids.length !== 1 ? 's' : ''} selected
                          </p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Button
                  type="button"
                  variant="outline"
                  onClick={addInvitation}
                  disabled={isSubmitting}
                  className="w-full"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Add Another User
                </Button>
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-secondary-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={isSubmitting}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>
                <Button
                  onClick={handleFinalSubmit}
                  disabled={isSubmitting}
                  className="min-w-[120px]"
                >
                  {isSubmitting ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      Create Organization
                      <Check className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Us Modal */}
      <ContactSalesModal
        isOpen={showContactSalesModal}
        onClose={() => {
          setShowContactSalesModal(false)
          setContactSalesPlan(null)
        }}
        plan={contactSalesPlan}
        organizationId={organizationData.id || null}
      />
    </div>
  )
}


